import styled, { useTheme } from 'styled-components';
import { P, PSmall } from '../elements/Text';
import Modal from '../Modal/Modal';
import Button from '../elements/Button';
import pluralize from 'pluralize';

const ModalWrapper = styled.div`
  padding: 12px;
  margin-bottom: 12px;
  width: 500px;
  margin-top: 16px;
`;

const ButtomGroup = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 16px;
`;

export default function ConfirmNextModal({
  isOpen,
  onClose,
  goToNextStep,
  isLastStep,
  missedQuestions,
}: {
  isOpen: boolean;
  onClose: () => void;
  goToNextStep: () => void;
  isLastStep: boolean;
  missedQuestions: Array<{
    stepIndex: number;
    questionIndex: number;
  }>;
}) {
  const theme = useTheme();
  if (!isOpen) return null;

  const totalMissedQuestions = missedQuestions.length;
  const totalMissedSteps = [
    ...new Set(missedQuestions.map((q) => q.stepIndex + 1)),
  ].length;

  return (
    <Modal
      underlayStyle={{
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
      }}
      style={{
        backgroundColor: theme.colors.bgPage2,
        width: 'fit-content',
        border: `1px solid ${theme.colors.bgActive}`,
      }}
    >
      <ModalWrapper>
        <P>Heads up! You missed one or more questions.</P>
        <PSmall>
          You can skip questions, but answering all of them will improve the
          accuracy of your assessment.
        </PSmall>
        {isLastStep && missedQuestions.length > 0 && (
          <PSmall>
            {(() => {
              // Helper function to create better copy based on the number of missed questions and steps
              const getMissedQuestionsText = () => {
                if (totalMissedQuestions === 1) {
                  return `You missed 1 question in step ${
                    missedQuestions[0].stepIndex + 1
                  }.`;
                }

                if (totalMissedSteps === 1) {
                  return `You missed ${totalMissedQuestions} questions in step ${
                    missedQuestions[0].stepIndex + 1
                  }.`;
                }

                // For multiple steps, be more concise
                if (totalMissedSteps <= 5) {
                  const stepNumbers = [
                    ...new Set(missedQuestions.map((q) => q.stepIndex + 1)),
                  ];
                  const stepsList = stepNumbers
                    .join(', ')
                    .replace(/,([^,]*)$/, ' and$1');
                  return `You missed ${totalMissedQuestions} ${pluralize(
                    'question',
                    totalMissedQuestions
                  )} across ${stepsList}.`;
                }

                // For many steps, use a more general message
                return `You missed ${totalMissedQuestions} ${pluralize(
                  'question',
                  totalMissedQuestions
                )} across ${totalMissedSteps} different steps.`;
              };

              return getMissedQuestionsText();
            })()}
          </PSmall>
        )}
        <ButtomGroup>
          <Button
            type="secondary"
            label="Go back"
            color={theme.colors.textActive}
            faIcon="fa-duotone fa-times"
            showIconBeforeLabel
            onClick={onClose}
          />
          <Button
            type="primary"
            label={isLastStep ? 'Finish' : 'Move to next step'}
            faIcon="fa-duotone fa-check"
            showIconBeforeLabel
            onClick={() => {
              goToNextStep();
              onClose();
            }}
          />
        </ButtomGroup>
      </ModalWrapper>
    </Modal>
  );
}
